import React, { useMemo } from 'react';

const SchemaParametersContext = React.createContext({});

/**
 * Supplies runtime values - a branch id, a customer id - to the schema fields rendered below it.
 * A field opts in by naming the ones it wants in its `contextParameters`.
 *
 * @param {Object} parameters - Values by parameter name.
 *
 * @returns {React.JSX.Element}
 */
export const SchemaParametersProvider = function SchemaParametersProvider({
  parameters = null,
  children,
}) {
  const value = useMemo(() => parameters ?? {}, [parameters]);

  return (
    <SchemaParametersContext.Provider value={value}>
      {children}
    </SchemaParametersContext.Provider>
  );
};

/**
 * Every runtime value in scope, by name.
 *
 * Serves the readers that address one by name rather than declaring the list they want up front.
 *
 * @returns {Object}
 */
export const useContextValues = () => React.useContext(SchemaParametersContext);

/**
 * The runtime parameters a field asks for, ready to merge into a request. Names with no value in
 * scope are left out.
 *
 * @param {Array} names - Parameter names, as declared in the field's `contextParameters`.
 *
 * @returns {Object}
 */
export const useContextParameters = (names = null) => {
  const parameters = React.useContext(SchemaParametersContext);

  return useMemo(
    () =>
      Object.fromEntries(
        (names ?? [])
          .filter((name) => parameters[name] !== undefined)
          .map((name) => [name, parameters[name]])
      ),
    [parameters, names]
  );
};
